/**
 * Client-side: the live map, postcode search, and the consent gate for ads. Talks
 * only to this same origin's /v1 API. Leaflet is bundled in (self-hosted, not a CDN)
 * so the map keeps working if a third-party CDN is down.
 */

import L from 'leaflet';
import { CATEGORY_COLOR, CATEGORY_LABEL, categorise } from './incidentSummary.ts';
import type { MarkerCategory } from './incidentSummary.ts';
import { PLANNED_FOOTER, isOpenPlanned, plannedCompletedText, plannedMetaRows } from './plannedPopup.ts';
import {
  ICON_CLOCK, ICON_PEOPLE_FILL, TIME_OPTS, filledIcon, formatDateTime, formatDuration, metaRow, outlineIcon,
} from './popupFormat.ts';
import { TREND_ARROW, TREND_WORD, classifyTrend } from '../../domain/trend.ts';
import type { Trend } from '../../domain/trend.ts';

// Self-hosted marker images, copied alongside this bundle -- see scripts/build-web.mjs.
L.Icon.Default.imagePath = '/vendor/images/';

interface OperatorFreshness {
  readonly operator: string;
  readonly name: string;
  readonly stale: boolean;
  readonly lastSuccessAt: string | null;
}

interface Incident {
  readonly id: string;
  readonly operator: {
    readonly id: string;
    readonly name: string;
    readonly attribution: string | null;
    readonly websiteUrl: string;
    readonly directPhone: string | null;
  };
  readonly status: string;
  readonly cause: string;
  readonly location: {
    readonly point: { readonly lon: number; readonly lat: number } | null;
    readonly areaName: string | null;
    readonly postcodeUnits: readonly string[];
    readonly postcodeSectors: readonly string[];
  };
  readonly customersAffected: number | null;
  readonly firstCustomersAffected: number | null;
  readonly description: string | null;
  readonly message: string | null;
  readonly startedAt: string | null;
  readonly restoredAt: string | null;
  readonly scheduledFor: string | null;
  readonly estimatedRestoration: {
    readonly claim:
      | { readonly kind: 'none' | 'to-be-confirmed' }
      | { readonly kind: 'point'; readonly at: string }
      | { readonly kind: 'window'; readonly from: string; readonly to: string };
    readonly displayText: string | null;
  };
}

interface IncidentsResponse {
  readonly generatedAt: string;
  readonly operators: readonly OperatorFreshness[];
  readonly data: readonly Incident[];
  readonly nextCursor: string | null;
}

interface IncidentsSummaryResponse {
  readonly generatedAt: string;
  readonly incidents: number;
  readonly planned: number;
  readonly customers: number | null;
  readonly typicalDurationMinutes: number | null;
}

interface PostcodeCheckResponse {
  readonly postcode: string;
  readonly matched: boolean;
  readonly incidents: readonly Incident[];
}

// Great Britain, roughly. Good enough as a default view before any data loads.
const DEFAULT_CENTER: L.LatLngExpression = [54.5, -3];
const DEFAULT_ZOOM = 6;

// Small inline popup icons specific to this file's own status badge -- the
// clock/people ones meta rows use are shared via popupFormat.ts instead, since
// plannedPopup.ts needs them too.
const ICON_WRENCH = '<path d="M20.2 7.7a6 6 0 0 1-7.9 7.9L6 22l-4-4 6.4-6.3a6 6 0 0 1 7.9-7.9L13 7l4 4Z"></path>';
const ICON_WARNING = '<path d="M12 2 L22 20 H2 Z"></path><path d="M12 9v5"></path><circle cx="12" cy="17" r="0.9"></circle>';
const ICON_CHECK = '<circle cx="12" cy="12" r="9"></circle><path d="M8 12.3 L12.3 15.6 L16 9"></path>';
const ICON_CALENDAR = '<rect x="3" y="5" width="18" height="16" rx="2"></rect><path d="M3 10h18M8 3v4M16 3v4"></path>';

/** currentColor so the status pill's own text colour (per category) is reused,
 * rather than a second colour to keep in sync with it. */
function badgeIcon(pathData: string): string {
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" width="12" height="12">${pathData}</svg>`;
}
const CATEGORY_ICON: Record<MarkerCategory, string> = {
  new: badgeIcon(ICON_WARNING),
  'in-progress': badgeIcon(ICON_WRENCH),
  planned: badgeIcon(ICON_CALENDAR),
  closed: badgeIcon(ICON_CHECK),
};

function restorationText(incident: Incident): string {
  const claim = incident.estimatedRestoration.claim;
  const display = incident.estimatedRestoration.displayText;
  if (display) return `Expected back ${display} (the operator's latest estimate — this may change)`;
  if (claim.kind === 'point') {
    return `Expected back by ${new Date(claim.at).toLocaleString('en-GB', { ...TIME_OPTS, day: '2-digit', month: 'short' })} (the operator's latest estimate — this may change)`;
  }
  if (claim.kind === 'window') {
    return `Expected back between ${new Date(claim.from).toLocaleTimeString('en-GB', TIME_OPTS)} and ${new Date(claim.to).toLocaleTimeString('en-GB', TIME_OPTS)} (the operator's latest estimate — this may change)`;
  }
  if (claim.kind === 'to-be-confirmed') return "There's no confirmed restoration time yet -- the operator hasn't given one.";
  return "There's no restoration estimate published yet.";
}

/** Just the time/phrase part of a concrete restoration estimate -- no "Expected
 * back by"/"between" prefix or parenthetical -- for the popup's own blue
 * estimate box (screen_mocks.png), which puts those in a separate label/note
 * either side of a short bold value instead of one long sentence. Null for
 * "to-be-confirmed"/"none", same cases restorationText() has no concrete time
 * for -- those stay the plain one-line .popup-restoration text, no box. */
function restorationEstimateValue(incident: Incident): string | null {
  const claim = incident.estimatedRestoration.claim;
  const display = incident.estimatedRestoration.displayText;
  if (display) return display;
  if (claim.kind === 'point') {
    return new Date(claim.at).toLocaleString('en-GB', { ...TIME_OPTS, day: '2-digit', month: 'short' });
  }
  if (claim.kind === 'window') {
    return `${new Date(claim.from).toLocaleTimeString('en-GB', TIME_OPTS)} – ${new Date(claim.to).toLocaleTimeString('en-GB', TIME_OPTS)}`;
  }
  return null;
}

/** For a closed incident, what actually happened beats the (now moot) estimate. */
function restorationOrClosedText(incident: Incident): string {
  if (incident.status !== 'restored') return restorationText(incident);
  if (incident.cause === 'planned') return plannedCompletedText(incident);
  if (incident.restoredAt === null) return 'Power restored.';
  const time = new Date(incident.restoredAt).toLocaleString('en-GB', { ...TIME_OPTS, day: '2-digit', month: 'short' });
  return `Power restored at ${time}`;
}

/**
 * Operator-supplied text (message, description, operator name) is external input and
 * goes into Leaflet's popup as innerHTML -- escape it, the same discipline as
 * src/web/html.ts on the server side.
 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function statusBadge(category: MarkerCategory): string {
  return `<span class="popup-status popup-status--${category}">${CATEGORY_ICON[category]}${CATEGORY_LABEL[category]}</span>`;
}

const MAX_POSTCODES_SHOWN = 6;

/** Full unit postcodes where the operator publishes them, else whatever sectors exist. */
function postcodesHtml(incident: Incident): string {
  const list = incident.location.postcodeUnits.length > 0
    ? incident.location.postcodeUnits
    : incident.location.postcodeSectors;
  if (list.length === 0) return '';
  const shown = list.slice(0, MAX_POSTCODES_SHOWN).map(escapeHtml).join(', ');
  const remaining = list.length - MAX_POSTCODES_SHOWN;
  const more = remaining > 0 ? ` <span class="popup-postcodes__more">+${remaining} more</span>` : '';
  return `<div class="popup-postcodes">${shown}${more}</div>`;
}

/**
 * A coloured teardrop pin, one per category -- a small inline SVG rather than a
 * separate image per colour, and cached so repeated markers of the same category
 * share one L.DivIcon instance rather than rebuilding it per marker.
 */
const PIN_ICONS = new Map<MarkerCategory, L.DivIcon>();

function pinIcon(category: MarkerCategory): L.DivIcon {
  const cached = PIN_ICONS.get(category);
  if (cached) return cached;
  const icon = L.divIcon({
    className: 'incident-pin',
    html:
      `<svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">` +
      `<path d="M12.5 0C5.6 0 0 5.6 0 12.5 0 21.9 12.5 41 12.5 41S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0Z" fill="${CATEGORY_COLOR[category]}"/>` +
      `<circle cx="12.5" cy="12.5" r="5" fill="#fff"/></svg>`,
    iconSize: [25, 41],
    iconAnchor: [12.5, 41],
    popupAnchor: [0, -36],
  });
  PIN_ICONS.set(category, icon);
  return icon;
}

function startedRow(incident: Incident): string {
  const icon = outlineIcon(ICON_CLOCK);
  if (incident.status === 'scheduled' && incident.scheduledFor !== null) {
    return metaRow('Scheduled for', formatDateTime(incident.scheduledFor), false, icon);
  }
  if (incident.startedAt !== null) return metaRow('Started', formatDateTime(incident.startedAt), false, icon);
  return metaRow('Started', 'Not published', true, icon);
}

/** null when there's nothing to compute a duration from at all (no startedAt, no
 * scheduledFor) -- distinct from a placeholder, which durationRow() renders. */
function durationValue(incident: Incident, now: number): string | null {
  if (incident.status === 'scheduled' && incident.scheduledFor !== null) {
    const startsAt = new Date(incident.scheduledFor).getTime();
    if (startsAt > now) return `Starts in ${formatDuration((startsAt - now) / 60_000)}`;
  }
  if (incident.startedAt === null) return null;
  const startedAt = new Date(incident.startedAt).getTime();
  if (incident.status === 'restored' && incident.restoredAt !== null) {
    const restoredAt = new Date(incident.restoredAt).getTime();
    return `Lasted ${formatDuration(Math.max(0, (restoredAt - startedAt) / 60_000))}`;
  }
  return `${formatDuration(Math.max(0, (now - startedAt) / 60_000))} ago`;
}

function durationRow(incident: Incident, now: number): string {
  const value = durationValue(incident, now);
  const icon = outlineIcon(ICON_CLOCK);
  return value !== null ? metaRow('Duration', value, false, icon) : metaRow('Duration', 'Not published', true, icon);
}

const TREND_CLASS: Record<Trend, string> = {
  worse: 'popup-trend--worse',
  better: 'popup-trend--better',
  steady: 'popup-trend--steady',
};

function trendHtml(current: number, first: number): string {
  const trend = classifyTrend(current, first);
  return `<span class="popup-trend ${TREND_CLASS[trend]}"><span class="popup-trend__arrow">${TREND_ARROW[trend]}</span>${TREND_WORD[trend]} (from ${first.toLocaleString('en-GB')})</span>`;
}

function customersRow(incident: Incident): string {
  const icon = filledIcon(ICON_PEOPLE_FILL, '#0B1F3B');
  if (incident.customersAffected === null) return metaRow('Customers affected', 'Not published', true, icon);
  const trend = incident.firstCustomersAffected !== null
    ? ` &middot; ${trendHtml(incident.customersAffected, incident.firstCustomersAffected)}`
    : '';
  return metaRow('Customers affected', `${incident.customersAffected.toLocaleString('en-GB')}${trend}`, false, icon);
}

function popupHtml(incident: Incident): string {
  const category = categorise(incident);
  const now = Date.now();
  // Open planned works get their own wording (plannedPopup.ts): a job is not a fault.
  const planned = isOpenPlanned(incident);
  const meta = planned
    ? plannedMetaRows(incident, now)
    : `${startedRow(incident)}${durationRow(incident, now)}${customersRow(incident)}`;
  // A concrete time gets its own blue callout (screen_mocks.png); planned works
  // and anything with no concrete time yet (to-be-confirmed/none/restored) keep
  // the plain one-line .popup-restoration text, same as before this pass.
  const estimateValue = !planned && incident.status !== 'restored' ? restorationEstimateValue(incident) : null;
  const footerHtml = estimateValue !== null
    ? `<div class="popup-estimate">`
      + `<p class="popup-estimate__label">${outlineIcon(ICON_CLOCK, '#3BA9FF', 16)}Expected back</p>`
      + `<p class="popup-estimate__value">${escapeHtml(estimateValue)}</p>`
      + `<p class="popup-estimate__note">This is the operator's latest estimate and may change.</p>`
      + `</div>`
    : `<div class="popup-restoration">${escapeHtml(planned ? PLANNED_FOOTER : restorationOrClosedText(incident))}</div>`;
  const parts = [
    statusBadge(category),
    `<div class="popup-operator">${escapeHtml(incident.operator.name)}</div>`,
    postcodesHtml(incident),
    `<div class="popup-meta">${meta}</div>`,
    footerHtml,
  ];
  const phone = incident.operator.directPhone
    ? ` or call <a href="tel:${escapeHtml(incident.operator.directPhone.replace(/\s+/g, ''))}">${escapeHtml(incident.operator.directPhone)}</a>`
    : '';
  parts.push(
    `<div class="popup-footer-row">`
    + `<div class="popup-contact">Report or check status on `
    + `<a href="${escapeHtml(incident.operator.websiteUrl)}" target="_blank" rel="noreferrer">${escapeHtml(incident.operator.name)}'s site</a>${phone}</div>`
    + `<a href="/power-cut/incident/${encodeURIComponent(incident.id)}" class="popup-view-details">View details &rarr;</a>`
    + `</div>`,
  );
  if (incident.operator.attribution) {
    parts.push(`<div class="popup-attribution">${escapeHtml(incident.operator.attribution)}</div>`);
  }
  return parts.filter((part) => part !== '').join('');
}

function renderIncidents(layer: L.LayerGroup, incidents: readonly Incident[]): void {
  layer.clearLayers();
  for (const incident of incidents) {
    const point = incident.location.point;
    if (!point) continue;
    L.marker([point.lat, point.lon], { icon: pinIcon(categorise(incident)) })
      .bindPopup(popupHtml(incident))
      .addTo(layer);
  }
}

function latestUpdateTime(operators: readonly OperatorFreshness[]): Date | null {
  const times = operators
    .map((o) => o.lastSuccessAt)
    .filter((t): t is string => t !== null)
    .map((t) => new Date(t).getTime());
  return times.length === 0 ? null : new Date(Math.max(...times));
}

function renderFreshness(operators: readonly OperatorFreshness[]): void {
  const el = document.getElementById('freshness-note');
  const textEl = el?.querySelector('.freshness__text');
  if (!el || !textEl) return;

  const delayed = operators
    .filter((o) => o.stale)
    .map((o) => o.name);
  const updated = latestUpdateTime(operators);
  const updatedText = updated ? `Updated ${updated.toLocaleTimeString('en-GB', TIME_OPTS)}` : null;

  el.hidden = false;
  el.classList.toggle('freshness--stale', delayed.length > 0);
  const parts = [updatedText, delayed.length > 0 ? `Delayed: ${delayed.join(', ')}` : null]
    .filter((part) => part !== null);
  textEl.textContent = parts.length > 0 ? parts.join(' · ') : 'Waiting for the first update';
}

/** A compact pill on mobile (value + label), a coloured-accent card on desktop
 * (label + big value + a third `detail` caption) -- same markup either way,
 * `.stat-card`'s own CSS (public/styles.css) reorders/hides `__detail` by
 * breakpoint. Each stat's own element keeps a stable id (`renderStats()`'s own
 * hidden-toggling targets it directly); this just builds what goes inside. */
function statCardHtml(label: string, value: string, detail: string): string {
  return `<span class="stat-card__label">${label}</span>`
    + `<span class="stat-card__value">${value}</span>`
    + `<span class="stat-card__detail">${detail}</span>`;
}

/**
 * A quick-glance summary right under the search box (moved up from below the map,
 * 2026-10-19). What the numbers mean (unplanned incidents only for volume /
 * customers / duration; planned works counted separately) lives server-side now
 * in summariseCurrentIncidents() (src/db/queries.ts), one aggregate SQL query --
 * same semantics as incidentSummary.ts's own summarise(), which this file still
 * uses for the map's own per-pin categorisation, just no longer for the stats
 * row. Changed 2026-10-25: the stats row used to wait on loadIncidents()
 * paginating through every current incident (a dozen+ sequential calls on a
 * high-volume day) before it could show anything -- found while investigating
 * the homepage feeling sluggish. Takes the server's response shape directly
 * (IncidentsSummaryResponse) rather than an Incident[] -- there's no longer a
 * client-side summarise() call on this path at all.
 */
function renderStats(summary: IncidentsSummaryResponse): void {
  const el = document.getElementById('stats-row');
  const totalEl = document.getElementById('stats-total');
  const customersEl = document.getElementById('stats-customers');
  const durationEl = document.getElementById('stats-duration');
  const plannedEl = document.getElementById('stats-planned');
  if (!el || !totalEl || !customersEl || !durationEl || !plannedEl) return;

  if (summary.incidents === 0 && summary.planned === 0) {
    // Stays in the layout (reserved height, see .stats-row in styles.css) --
    // just nothing to show, so keep it invisible rather than collapsing it.
    el.classList.add('stats-row--loading');
    return;
  }

  el.classList.remove('stats-row--loading');
  totalEl.innerHTML = statCardHtml(
    'Active incidents', String(summary.incidents), 'Unplanned, right now',
  );
  // With no unplanned incident there is no customer total or duration to speak of.
  customersEl.hidden = summary.incidents === 0;
  durationEl.hidden = summary.incidents === 0;
  customersEl.innerHTML = summary.customers !== null
    ? statCardHtml('Customers affected', summary.customers.toLocaleString('en-GB'), 'Across active incidents')
    : statCardHtml('Customers affected', '—', 'Not published by the operator');
  durationEl.innerHTML = summary.typicalDurationMinutes !== null
    ? statCardHtml('Typical duration', formatDuration(summary.typicalDurationMinutes), 'Median, right now')
    : statCardHtml('Typical duration', '—', 'Start times not published');
  plannedEl.hidden = summary.planned === 0;
  plannedEl.innerHTML = statCardHtml('Planned works', String(summary.planned), 'Scheduled maintenance');
}

/**
 * Follows every page: with more incidents live than fit on one page, stopping after
 * page one would silently drop whichever operators sort last by id -- currently all
 * of UKPN, since ids sort as `${operator}:${reference}` and "ukpn" sorts last among
 * the four built so far.
 */
/** A regional SEO page (/power-cut/:slug, src/web/pages.ts's areaPage) embeds its
 * own already-server-rendered incident/freshness data as a JSON script tag rather
 * than a live fetch -- the same data the page's own crawlable HTML content was
 * built from, so there's one source of truth per request instead of the map
 * silently disagreeing with the text above it. No auto-refresh (unlike the
 * homepage's loadIncidents) -- a visitor gets a fresh copy on their next page
 * load/crawl, which is the right cadence for a landing page, not a live dashboard. */
function loadAreaIncidents(layer: L.LayerGroup, map: L.Map): void {
  const el = document.getElementById('area-data');
  if (!(el instanceof HTMLScriptElement)) return;
  let body: {
    incidents: readonly Incident[];
    operators: readonly OperatorFreshness[];
    boundary?: GeoJSON.GeometryObject | null;
  };
  try {
    body = JSON.parse(el.textContent) as typeof body;
  } catch {
    return;
  }
  drawAreaBoundary(map, body.boundary ?? null);
  renderIncidents(layer, body.incidents);
  renderFreshness(body.operators);
}

/**
 * Outlines the geography a regional page covers: the ONS built-up-area polygon, simplified for
 * drawing server-side (getAreaDisplayBoundary). Not interactive, so it never swallows a click meant
 * for a marker. Fits the map to it (capped, so a small town is not zoomed to street level).
 * Dark navy, not blue: blue is the planned-works pin colour.
 */
function drawAreaBoundary(map: L.Map, boundary: GeoJSON.GeometryObject | null): void {
  if (!boundary) return;
  const outline = L.geoJSON(boundary, {
    interactive: false,
    style: { color: '#0b1f3b', weight: 2, opacity: 0.85, fillColor: '#0b1f3b', fillOpacity: 0.06 },
  }).addTo(map);
  const bounds = outline.getBounds();
  // Extra bottom padding: the freshness note overlays the bottom of the map (up to three lines on a
  // narrow one) and must not sit on top of the outline.
  if (bounds.isValid()) map.fitBounds(bounds, { paddingTopLeft: [24, 24], paddingBottomRight: [24, 112], maxZoom: 14 });
  map.attributionControl.addAttribution('Boundary: ONS built-up areas, contains OS data (OGL v3)');
  addMapKeyOutlineRow();
}

/** Adds the "Area covered" row to the legend below the map, once a boundary actually loads
 * (mapLegend() in pages.ts server-renders the other rows up front, before this data exists). */
function addMapKeyOutlineRow(): void {
  const legend = document.querySelector('.map-legend');
  if (!legend || legend.querySelector('.map-legend__swatch--outline')) return;
  const row = document.createElement('div');
  row.className = 'map-legend__row';
  row.innerHTML = '<span class="map-legend__swatch map-legend__swatch--outline"></span>'
    + '<span><strong>Area covered</strong> <span class="map-legend__meaning">The built-up area this page is about</span></span>';
  legend.appendChild(row);
}

async function loadIncidents(layer: L.LayerGroup): Promise<void> {
  const incidents: Incident[] = [];
  let freshness: readonly OperatorFreshness[] = [];
  let cursor: string | null = null;

  for (;;) {
    const url = cursor === null
      ? '/v1/incidents?limit=100'
      : `/v1/incidents?limit=100&cursor=${encodeURIComponent(cursor)}`;
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) break;
    const body = (await res.json()) as IncidentsResponse;
    incidents.push(...body.data);
    freshness = body.operators;
    if (body.nextCursor === null) break;
    cursor = body.nextCursor;
  }

  renderIncidents(layer, incidents);
  renderFreshness(freshness);
}

/** The stats row's own data source -- a single, fast request, deliberately not
 * tied to loadIncidents()'s own pagination loop above (see renderStats' own
 * comment on why). Fetched and rendered independently so the stats row can
 * appear well before the map's own full incident set has finished paginating
 * in, on a day with enough incidents for that to take a while. */
async function loadStats(): Promise<void> {
  const res = await fetch('/v1/incidents/summary', { cache: 'no-store' });
  if (!res.ok) return;
  const summary = (await res.json()) as IncidentsSummaryResponse;
  renderStats(summary);
}

interface MapHistoryIncident {
  readonly id: string;
  readonly operator: string;
  readonly lon: number;
  readonly lat: number;
  readonly startedAt: string;
  readonly peak: number | null;
}
interface MapHistoryResponse {
  readonly generatedAt: string;
  readonly windowFrom: string;
  readonly windowTo: string;
  readonly incidents: readonly MapHistoryIncident[];
}

/** Flash colour by peak customers affected -- same four tiers and reasoning
 * as the concept mock-up (lightningmaps.org-inspired), reusing this site's
 * own brand reds/ambers (incidentSummary.ts's MARKER_KEY) rather than a new
 * palette: a flash is still "a power cut", just animated by when it started
 * instead of its current live status. */
function flashColor(peak: number | null): string {
  const p = peak ?? 0;
  if (p >= 1000) return '#FF3B30'; // same red as the live map's "New" pin
  if (p >= 300) return '#FF6A3D';
  if (p >= 50) return '#F59E0B'; // same amber as the live map's "In progress" pin
  return '#FFD166';
}

/** Area-proportional-ish (sqrt), clamped so a single huge incident can't
 * swamp the map. */
function flashMaxRadius(peak: number | null): number {
  const r = 5 + Math.sqrt(Math.max(peak ?? 1, 1)) * 1.5;
  return Math.min(r, 60);
}

const FLASH_LIFE_MS = 1300;

// Filled triangle/bars (play/pause) and a stroke X (close) -- icon buttons
// rather than text ones so the whole control fits on one line at any
// width, not just desktop (user feedback, 2026-10-24: text buttons wrapped
// unevenly on a phone). aria-label carries the meaning text content no
// longer does.
const ICON_PLAY = '<path d="M8 5v14l11-7z"></path>';
const ICON_PAUSE = '<path d="M6 5h4v14H6zM14 5h4v14h-4z"></path>';
const ICON_CLOSE = '<path d="M18 6 6 18"></path><path d="M6 6l12 12"></path>';

/**
 * A plain canvas laid over the Leaflet map (not a Leaflet layer/pane -- a
 * flash is a screen-space animation redrawn every frame, which a DOM-based
 * marker layer would do far more expensively) that draws and ages out
 * expanding, fading ripples at given lat/lons. Repositions itself from the
 * map's own `latLngToContainerPoint` on every draw, so panning/zooming the
 * live map under an active replay "just works" with no projection math of
 * our own (see this session's plan for why the concept mock-up needed one
 * and the real map doesn't).
 */
function createFlashLayer(map: L.Map): {
  readonly canvas: HTMLCanvasElement;
  spawn(lat: number, lon: number, peak: number | null): void;
  draw(now: number): void;
  clear(): void;
  destroy(): void;
} {
  const container = map.getContainer();
  const canvas = document.createElement('canvas');
  canvas.style.cssText = 'position:absolute;inset:0;pointer-events:none;z-index:650;';
  container.appendChild(canvas);
  const ctx = canvas.getContext('2d');

  interface Flash { readonly lat: number; readonly lon: number; readonly color: string; readonly maxR: number; readonly bornAt: number; }
  const flashes: Flash[] = [];

  function resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = container.getBoundingClientRect();
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
  }
  resize();
  map.on('resize move zoom', resize);

  return {
    canvas,
    spawn(lat, lon, peak) {
      flashes.push({ lat, lon, color: flashColor(peak), maxR: flashMaxRadius(peak), bornAt: performance.now() });
    },
    draw(now) {
      if (!ctx) return;
      const rect = container.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);
      for (let i = flashes.length - 1; i >= 0; i--) {
        const f = flashes[i];
        if (!f) continue;
        const t = Math.min((now - f.bornAt) / FLASH_LIFE_MS, 1);
        if (t >= 1) { flashes.splice(i, 1); continue; }
        const point = map.latLngToContainerPoint([f.lat, f.lon]);
        const eased = 1 - Math.pow(1 - t, 2);
        const r = 3 + eased * f.maxR;
        ctx.beginPath();
        ctx.arc(point.x, point.y, r, 0, Math.PI * 2);
        ctx.strokeStyle = f.color;
        ctx.globalAlpha = (1 - t) * 0.9;
        ctx.lineWidth = 2;
        ctx.stroke();
        if (t < 0.35) {
          ctx.beginPath();
          ctx.arc(point.x, point.y, Math.max(r * 0.3, 2.5), 0, Math.PI * 2);
          ctx.fillStyle = f.color;
          ctx.globalAlpha = 1 - t / 0.35;
          ctx.fill();
        }
        ctx.globalAlpha = 1;
      }
    },
    clear() {
      flashes.length = 0;
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
    },
    destroy() {
      map.off('resize move zoom', resize);
      canvas.remove();
    },
  };
}

/** How long a full replay takes at 1x speed, regardless of how many real
 * hours/days the window spans -- a 31-day window played at real speed would
 * take 31 days, so this compresses it into a watchable loop. */
const REPLAY_DURATION_S = 70;

/**
 * The play/pause/slider/timestamp chrome and simulated-clock playback shared
 * by both replay modes below -- the homepage's own "Replay the last 12 hours
 * of incidents" (rolling, click-to-reveal, with a "Back to live" exit) and a
 * news article's fixed-window one (auto-shown, no live layer to go back to).
 * `onExit`, when given, adds the exit button; omitted, there isn't one.
 * Starts already showing every incident that started in the window (the
 * timeline's own end), same as both callers want -- the fullest, most
 * complete picture visible before anyone touches Play, with Play available
 * to watch it build up from the start.
 */
function buildReplayPanel(
  windowFrom: string,
  windowTo: string,
  incidents: readonly MapHistoryIncident[],
  flashLayer: ReturnType<typeof createFlashLayer>,
  onExit?: () => void,
): HTMLElement {
  const sorted = incidents.slice().sort((a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime());
  const t0 = new Date(windowFrom).getTime();
  const t1 = new Date(windowTo).getTime();
  const totalMs = Math.max(t1 - t0, 1);
  const SCRUB_MAX = 1000;

  let simMs = t1;
  let playing = false;
  let rafId: number | null = null;
  let lastTick: number | null = null;
  let nextIndex = sorted.length;

  const wrap = document.createElement('div');
  wrap.className = 'replay-control__wrap';

  const el = document.createElement('div');
  el.className = 'replay-control__panel';
  el.innerHTML =
    `<button type="button" class="replay-control__icon-btn replay-control__play" aria-label="Play">${filledIcon(ICON_PLAY, 'currentColor', 15)}</button>` +
    `<input type="range" class="replay-control__slider" min="0" max="${SCRUB_MAX}" value="${SCRUB_MAX}">` +
    `<span class="replay-control__time"></span>` +
    (onExit ? `<button type="button" class="replay-control__icon-btn replay-control__exit" aria-label="Back to live view">${outlineIcon(ICON_CLOSE, 'currentColor', 15)}</button>` : '');
  wrap.appendChild(el);

  if (onExit) {
    const hint = document.createElement('p');
    hint.className = 'replay-control__hint';
    hint.textContent = 'Click Close to switch back to Live view';
    wrap.appendChild(hint);
  }

  const legend = document.createElement('div');
  legend.className = 'replay-control__legend';
  legend.innerHTML =
    `<span class="replay-control__legend-title">Flash size = peak affected</span>` +
    `<span class="replay-control__legend-item"><span class="replay-control__legend-dot" style="width:8px;height:8px;background:#FFD166"></span>under 50</span>` +
    `<span class="replay-control__legend-item"><span class="replay-control__legend-dot" style="width:12px;height:12px;background:#F59E0B"></span>50&ndash;300</span>` +
    `<span class="replay-control__legend-item"><span class="replay-control__legend-dot" style="width:16px;height:16px;background:#FF6A3D"></span>300&ndash;1,000</span>` +
    `<span class="replay-control__legend-item"><span class="replay-control__legend-dot" style="width:20px;height:20px;background:#FF3B30"></span>over 1,000</span>`;
  wrap.appendChild(legend);

  const playButton = el.querySelector('.replay-control__play');
  const slider = el.querySelector('.replay-control__slider');
  const timeLabel = el.querySelector('.replay-control__time');
  const exitButton = el.querySelector('.replay-control__exit');

  function fmt(ms: number): string {
    return new Date(ms).toLocaleString('en-GB', { ...TIME_OPTS, day: '2-digit', month: 'short' });
  }

  function setPlayingUi(isPlaying: boolean): void {
    if (playButton instanceof HTMLButtonElement) {
      playButton.innerHTML = filledIcon(isPlaying ? ICON_PAUSE : ICON_PLAY, 'currentColor', 15);
      playButton.setAttribute('aria-label', isPlaying ? 'Pause' : 'Play');
    }
  }

  /** Pauses the simulated clock -- the draw loop itself (`tick`, started
   * once below) keeps running regardless, same as the concept mock-up's own
   * always-on rAF loop, so a flash mid-fade when paused finishes fading
   * instead of freezing, and the canvas keeps repositioning on map pan/zoom.
   * Only `destroy()` (panel removed entirely -- "Back to live" or the panel
   * being torn down) actually cancels it. */
  function stopPlaying(): void {
    playing = false;
    setPlayingUi(false);
    lastTick = null;
  }

  function destroy(): void {
    if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
  }

  /** Jumps straight to `ms`, re-seeding which flashes have already "happened"
   * without animating through the ones in between -- used by the slider
   * (scrubbing shouldn't replay every flash since 0) and on first render. */
  function jumpTo(ms: number): void {
    simMs = Math.max(t0, Math.min(t1, ms));
    flashLayer.clear();
    nextIndex = 0;
    while (nextIndex < sorted.length && new Date(sorted[nextIndex]?.startedAt ?? 0).getTime() <= simMs) nextIndex++;
    if (slider instanceof HTMLInputElement) slider.value = String(((simMs - t0) / totalMs) * SCRUB_MAX);
    if (timeLabel) timeLabel.textContent = fmt(simMs);
  }

  function spawnDue(): void {
    while (nextIndex < sorted.length) {
      const inc = sorted[nextIndex];
      if (!inc || new Date(inc.startedAt).getTime() > simMs) break;
      flashLayer.spawn(inc.lat, inc.lon, inc.peak);
      nextIndex++;
    }
  }

  function tick(now: number): void {
    rafId = requestAnimationFrame(tick);
    if (playing) {
      if (lastTick === null) lastTick = now;
      const dtMs = now - lastTick;
      lastTick = now;
      const msPerRealMs = totalMs / (REPLAY_DURATION_S * 1000);
      simMs += dtMs * msPerRealMs;
      if (simMs >= t1) {
        simMs = t1;
        stopPlaying();
      }
      spawnDue();
      if (slider instanceof HTMLInputElement) slider.value = String(((simMs - t0) / totalMs) * SCRUB_MAX);
      if (timeLabel) timeLabel.textContent = fmt(simMs);
    }
    flashLayer.draw(now);
  }

  if (slider instanceof HTMLInputElement) {
    slider.addEventListener('input', () => {
      stopPlaying();
      jumpTo(t0 + (Number(slider.value) / SCRUB_MAX) * totalMs);
    });
  }

  if (playButton instanceof HTMLButtonElement) {
    playButton.addEventListener('click', () => {
      if (playing) { stopPlaying(); return; }
      if (simMs >= t1) jumpTo(t0);
      playing = true;
      setPlayingUi(true);
      lastTick = null;
    });
  }

  if (exitButton instanceof HTMLButtonElement && onExit) {
    exitButton.addEventListener('click', () => {
      stopPlaying();
      destroy();
      onExit();
    });
  }

  jumpTo(t1);
  rafId = requestAnimationFrame(tick);
  return wrap;
}

/**
 * "Replay the last 12 hours of incidents" -- a time-slider/play button over
 * the live map. Each incident flashes and ripples outward the moment it
 * started, sized/coloured by its peak customer count -- inspired by
 * lightningmaps.org (see this session's plan for the concept mock-up this
 * was built from). A rolling window relative to now (GET /v1/map-history
 * with no from/to) -- see wireArticleReplayMap below for the fixed-window
 * sibling a news article uses instead.
 *
 * The live `layer` is hidden (not cleared) while replaying, and restored
 * exactly as it was on "Back to live".
 */
function wireReplayControl(map: L.Map, liveLayer: L.LayerGroup): void {
  const root = document.getElementById('replay-control');
  const toggleEl = document.getElementById('replay-toggle');
  if (!root || !(toggleEl instanceof HTMLButtonElement)) return;
  const toggle: HTMLButtonElement = toggleEl;

  let data: MapHistoryResponse | null = null;
  let panel: HTMLElement | null = null;
  let flashLayer: ReturnType<typeof createFlashLayer> | null = null;

  toggle.addEventListener('click', () => {
    toggle.hidden = true;
    toggle.disabled = true;

    const load = data
      ? Promise.resolve()
      : fetch('/v1/map-history', { cache: 'no-store' })
        .then(async (res) => {
          if (!res.ok) throw new Error('map-history fetch failed');
          data = (await res.json()) as MapHistoryResponse;
        });

    load
      .then(() => {
        toggle.disabled = false;
        if (!data) { toggle.hidden = false; return; }
        const loaded = data;
        liveLayer.remove();
        flashLayer = createFlashLayer(map);
        panel = buildReplayPanel(loaded.windowFrom, loaded.windowTo, loaded.incidents, flashLayer, () => {
          flashLayer?.destroy();
          flashLayer = null;
          liveLayer.addTo(map);
          panel?.remove();
          toggle.hidden = false;
        });
        root.appendChild(panel);
      })
      .catch(() => {
        toggle.hidden = false;
        toggle.disabled = false;
      });
  });
}

/**
 * A news article's embedded replay map (src/web/pages.ts's newsReplayMapSection,
 * `#map[data-replay-from][data-replay-to]`) -- a fixed window tied to that
 * specific post (GET /v1/map-history?from=...&to=...), auto-shown on load with
 * no toggle and no "Back to live" (there's no live layer on a static article).
 * Fits the map to the window's own incidents once loaded, rather than the
 * homepage's fixed nationwide default view -- a single event's own footprint is
 * usually much smaller than all of GB.
 */
function wireArticleReplayMap(map: L.Map, from: string, to: string): void {
  const root = document.getElementById('replay-control');
  if (!root) return;

  fetch(`/v1/map-history?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, { cache: 'no-store' })
    .then(async (res) => {
      if (!res.ok) return;
      const body = (await res.json()) as MapHistoryResponse;
      if (body.incidents.length === 0) return;

      const points: L.LatLngTuple[] = body.incidents.map((i) => [i.lat, i.lon]);
      map.fitBounds(L.latLngBounds(points), { padding: [24, 24], maxZoom: 12 });

      const flashLayer = createFlashLayer(map);
      root.appendChild(buildReplayPanel(body.windowFrom, body.windowTo, body.incidents, flashLayer));
    })
    .catch(() => { /* leave the map at its default view rather than erroring visibly on a static article */ });
}

/** `response.postcode` is server-normalised (the same `parsePostcodeInput` every
 * caller shares), not raw user input -- escaped anyway for the same discipline
 * this file's own popup-content comment states, not because it's expected to
 * carry anything unsafe. */
function postcodePermalink(postcode: string): string {
  return `<a href="/postcode/${encodeURIComponent(postcode)}" class="postcode-result__permalink">View the full page for ${escapeHtml(postcode)} &rarr;</a>`;
}

function renderPostcodeResult(response: PostcodeCheckResponse, map: L.Map): void {
  const el = document.getElementById('postcode-result');
  if (!el) return;
  el.hidden = false;
  const permalink = postcodePermalink(response.postcode);

  if (!response.matched) {
    el.innerHTML = `<p>No reported power cuts currently affect ${escapeHtml(response.postcode)}.</p>${permalink}`;
    el.className = 'postcode-result postcode-result--clear';
    return;
  }

  const open = response.incidents.filter((i) => categorise(i) !== 'closed');

  if (open.length > 0) {
    el.innerHTML = `<p>${open.length} power cut${open.length === 1 ? '' : 's'} reported affecting ${escapeHtml(response.postcode)}.</p>${permalink}`;
    el.className = 'postcode-result postcode-result--affected';
  } else {
    // Everything matched is 'closed' -- restored within the API's own recency window.
    el.innerHTML = `<p>Power has recently been reported as restored near ${escapeHtml(response.postcode)}.</p>${permalink}`;
    el.className = 'postcode-result postcode-result--clear';
  }

  const first = response.incidents[0]?.location.point;
  if (first) map.setView([first.lat, first.lon], 13);
}

/** Shared by the form's own submit handler and the ?postcode= handoff (below) --
 * a postcode-area page's "Check your postcode" card has no #map of its own, so it's
 * a plain navigating form to /?postcode=..., and this is what runs once that lands
 * back here. */
function runPostcodeCheck(postcode: string, map: L.Map): void {
  const el = document.getElementById('postcode-result');
  if (el) {
    el.hidden = false;
    el.textContent = 'Checking for power cuts near you…';
    el.className = 'postcode-result';
  }

  fetch(`/v1/postcode-check?postcode=${encodeURIComponent(postcode)}`, { cache: 'no-store' })
    .then(async (res) => {
      if (!el) return;
      el.hidden = false;
      if (res.ok) {
        renderPostcodeResult(await res.json() as PostcodeCheckResponse, map);
      } else {
        const problem = await res.json() as ProblemJson;
        el.textContent = problem.detail;
        el.className = 'postcode-result postcode-result--error';
      }
    })
    .catch(() => {
      if (el) {
        el.hidden = false;
        el.textContent = "We can't check the latest information right now. Please try again shortly.";
        el.className = 'postcode-result postcode-result--error';
      }
    });
}

/** ?postcode= in the URL -- the handoff target for the plain navigating postcode
 * form on pages with no #map of their own (currently just /postcode-area, user
 * decision 2026-10-08): pre-fills the input and runs the same check immediately,
 * rather than landing a visitor on a blank homepage after they already typed their
 * postcode once. Left in the URL (not stripped via history.replaceState) so the
 * result stays reproducible on reload/share, same as any other GET search. */
function applyPostcodeFromQuery(map: L.Map): void {
  const postcode = new URLSearchParams(location.search).get('postcode');
  if (!postcode) return;
  const input = document.getElementById('postcode-input');
  if (input instanceof HTMLInputElement) input.value = postcode;
  runPostcodeCheck(postcode, map);
}

function wirePostcodeSearch(map: L.Map): void {
  const form = document.getElementById('postcode-form');
  if (!(form instanceof HTMLFormElement)) return;

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const input = form.elements.namedItem('postcode');
    if (!(input instanceof HTMLInputElement) || input.value.trim() === '') return;
    runPostcodeCheck(input.value, map);
  });
}

interface VoteResponse {
  recorded: boolean;
  votes?: number;
}

/** The "Yes, I'm affected" button (pages.ts's own voteSection) -- shared by the
 * homepage's major outage card and, since 2026-10-19, the incident detail page,
 * so this wires every `.vote-btn` on the page rather than one hardcoded id (the
 * two never actually appear together, but nothing stops a future third place
 * reusing the same markup). No-ops on a page with none. The server already
 * renders the "already voted" state directly when the request's own cookie
 * says so (router.ts's own alreadyVoted checks), so this only ever needs to
 * handle a fresh click, not an already-voted page load. Disables the button
 * immediately on click (before the request resolves) so a slow connection
 * can't be double-clicked into two requests -- the server's own cookie check
 * would still no-op a genuine repeat, but there's no reason to rely on that
 * for the common case. */
function wireVoteButtons(): void {
  document.querySelectorAll<HTMLButtonElement>('.vote-btn').forEach((button) => {
    const incidentId = button.dataset['incidentId'];
    const countTarget = button.dataset['voteCountTarget'];
    if (!incidentId || !countTarget) return;

    button.addEventListener('click', () => {
      button.disabled = true;
      fetch(`/incidents/${encodeURIComponent(incidentId)}/vote`, { method: 'POST' })
        .then(async (res) => {
          if (!res.ok) { button.disabled = false; return; }
          const body = await res.json() as VoteResponse;
          const countEl = document.getElementById(countTarget);
          if (countEl && typeof body.votes === 'number') {
            countEl.innerHTML = `<strong>${body.votes.toLocaleString('en-GB')}</strong> confirmed affected`;
          }
          button.outerHTML = '<span class="voted">✓ Thanks, recorded</span>';
        })
        .catch(() => { button.disabled = false; });
    });
  });
}

/** The nation page's postcode-areas table (nationAreasTable(), pages.ts) -- a
 * plain progressive enhancement over the fully server-rendered table, not
 * something the page depends on JS for: with this script absent, every row is
 * still there, just unfiltered and in server order. No-ops when the table
 * isn't on the page (every other tier this script also runs on). */
/** England alone has ~40 rows -- collapsed to this many by default, same
 * number pages.ts's own NATION_AREAS_COLLAPSED_COUNT uses for the "Show more"
 * link's server-rendered presence/absence. Kept in sync by eye (two small
 * constants, not worth threading through a shared module for). */
const NATION_AREAS_COLLAPSED_COUNT = 10;

function wireNationAreasTable(): void {
  const table = document.getElementById('nation-areas-table') as HTMLTableElement | null;
  const filterInput = document.getElementById('nation-areas-filter') as HTMLInputElement | null;
  const sortSelect = document.getElementById('nation-areas-sort') as HTMLSelectElement | null;
  const showMoreLink = document.getElementById('nation-areas-show-more');
  if (!table || !filterInput || !sortSelect) return;
  const tbody = table.tBodies[0];
  if (!tbody) return;
  const rows = Array.from(tbody.rows);
  let expanded = false;

  function apply(): void {
    const query = (filterInput?.value ?? '').trim().toLowerCase();
    const filtering = query !== '';
    const sortBy = sortSelect?.value ?? 'total';
    const sorted = rows.slice().sort((a, b) => {
      if (sortBy === 'name') return (a.dataset['name'] ?? '').localeCompare(b.dataset['name'] ?? '');
      const key = sortBy === 'current' ? 'current' : 'total';
      return Number(b.dataset[key] ?? 0) - Number(a.dataset[key] ?? 0);
    });
    for (const row of sorted) tbody?.appendChild(row);

    // While actively filtering, every match shows regardless of the collapse --
    // a filtered-out "Show more" would be confusing (why isn't my search result
    // here?). Otherwise, collapsed to NATION_AREAS_COLLAPSED_COUNT until expanded.
    let shown = 0;
    for (const row of sorted) {
      const matches = query === '' ||
        (row.dataset['name']?.includes(query) ?? false) ||
        (row.dataset['code']?.includes(query) ?? false);
      if (!matches) { row.hidden = true; continue; }
      const collapse = !filtering && !expanded && shown >= NATION_AREAS_COLLAPSED_COUNT;
      row.hidden = collapse;
      if (!collapse) shown++;
    }
    if (showMoreLink) showMoreLink.hidden = filtering || expanded || rows.length <= NATION_AREAS_COLLAPSED_COUNT;
  }

  filterInput.addEventListener('input', apply);
  sortSelect.addEventListener('change', apply);
  showMoreLink?.addEventListener('click', (event) => {
    event.preventDefault();
    expanded = true;
    apply();
  });
  apply();
}

/** The incident detail page's own small, static map -- a single marker/shape, not
 * the live, polling, nationwide map `main()`'s own early-return guards against
 * running twice on the same page. Deliberately a separate element id
 * (#incident-map, not #map) and a separate code path from drawAreaBoundary/
 * loadAreaIncidents above, rather than a third branch of that dispatch -- this map
 * has nothing to poll and no incident list to draw, just one place. ENWL incidents
 * carry a real polygon (data-incident-shape, GeoJSON); every other operator only
 * ever has a point, drawn as a soft, clearly-approximate circle -- see
 * incidentDetailPage()'s own comment on why no other shape is invented. */
function wireIncidentMap(): void {
  const mapEl = document.getElementById('incident-map');
  if (!mapEl) return;
  const lon = Number(mapEl.dataset['incidentLon']);
  const lat = Number(mapEl.dataset['incidentLat']);
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;

  const map = L.map(mapEl).setView([lat, lon], 13);
  L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 19,
  }).addTo(map);

  const shapeJson = mapEl.dataset['incidentShape'];
  if (shapeJson) {
    try {
      const shape = JSON.parse(shapeJson) as GeoJSON.GeometryObject;
      const outline = L.geoJSON(shape, {
        interactive: false,
        style: { color: '#ff3b30', weight: 2, opacity: 0.85, fillColor: '#ff3b30', fillOpacity: 0.12 },
      }).addTo(map);
      const bounds = outline.getBounds();
      if (bounds.isValid()) map.fitBounds(bounds, { padding: [24, 24], maxZoom: 15 });
      return;
    } catch {
      // Malformed shape data -- fall through to the point-only marker below.
    }
  }
  L.circle([lat, lon], {
    radius: 800,
    interactive: false,
    color: '#ff3b30',
    weight: 2,
    opacity: 0.85,
    fillColor: '#ff3b30',
    fillOpacity: 0.12,
  }).addTo(map);
  L.marker([lat, lon]).addTo(map);
}

/** Small, non-interactive per-card map previews on the shared incident summary
 * card (.js-incident-card-minimap) -- used on Operator, Country, Local Power
 * Cut and postcode-area pages alike. A point marker only, never a real
 * polygon: none of this card's list queries select shape_geojson, so there's
 * nothing to draw but the point even for ENWL rows. All interaction disabled
 * (zoom/drag/scroll/etc.) since these are previews that link to the
 * incident's own full map, not a map to use in place. */
function wireIncidentCardMinimaps(): void {
  document.querySelectorAll<HTMLElement>('.js-incident-card-minimap').forEach((el) => {
    const lon = Number(el.dataset['lon']);
    const lat = Number(el.dataset['lat']);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;

    const map = L.map(el, {
      zoomControl: false,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
      attributionControl: false,
      touchZoom: false,
    }).setView([lat, lon], 13);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);
    L.circle([lat, lon], {
      radius: 800,
      interactive: false,
      color: '#ff3b30',
      weight: 2,
      opacity: 0.85,
      fillColor: '#ff3b30',
      fillOpacity: 0.12,
    }).addTo(map);
  });
}

/** "Share this incident" -- native share sheet where the browser supports it
 * (covers WhatsApp/Messages/etc. on most mobile browsers without a separate deep
 * link each), "Copy link" everywhere via the Clipboard API. The WhatsApp/Facebook
 * buttons themselves are plain `<a href>` deep links (incidentDetailPage()'s own
 * markup) -- no JS needed for those, only the native-share toggle and copy button
 * here. */
function wireIncidentShare(): void {
  const toggle = document.querySelector('.incident-detail-share-toggle');
  if (toggle instanceof HTMLButtonElement) {
    const shareUrl = toggle.dataset['shareUrl'];
    const shareTitle = toggle.dataset['shareTitle'];
    if (shareUrl && typeof navigator.share === 'function') {
      toggle.addEventListener('click', () => {
        void navigator.share({ ...(shareTitle ? { title: shareTitle } : {}), url: shareUrl });
      });
    } else {
      // No native share sheet (most desktop browsers) -- scroll to the always-
      // present WhatsApp/Facebook/Copy link row below instead of no-opping.
      toggle.addEventListener('click', () => {
        document.querySelector('.incident-detail-share')?.scrollIntoView({ behavior: 'smooth' });
      });
    }
  }

  const copyButton = document.querySelector('[data-copy-link]');
  if (copyButton instanceof HTMLButtonElement) {
    const url = copyButton.dataset['copyLink'];
    if (!url) return;
    const originalText = copyButton.textContent;
    copyButton.addEventListener('click', () => {
      navigator.clipboard.writeText(url)
        .then(() => {
          copyButton.textContent = 'Copied!';
          setTimeout(() => { copyButton.textContent = originalText; }, 2000);
        })
        .catch(() => { /* Clipboard API unavailable/denied -- the link itself is still visible in the address bar. */ });
    });
  }
}

/** The news post hero's own "Share this article" button -- same visual style
 * (.btn.btn--ghost plus the share icon) and native-share-first approach as
 * wireIncidentShare() above, but a separate, simpler wiring: a news post has
 * no always-visible WhatsApp/Facebook/Copy row to scroll to, so the
 * non-native-share fallback copies the link directly instead, reusing the
 * same "Copied!" micro-interaction the incident page's own copy-link button
 * already has (src/web/pages.ts's newsPostPage()). */
function wireNewsShare(): void {
  const button = document.querySelector('.news-hero__share');
  if (!(button instanceof HTMLButtonElement)) return;
  const shareUrl = button.dataset['shareUrl'];
  if (!shareUrl) return;
  const shareTitle = button.dataset['shareTitle'];

  if (typeof navigator.share === 'function') {
    button.addEventListener('click', () => {
      void navigator.share({ ...(shareTitle ? { title: shareTitle } : {}), url: shareUrl });
    });
    return;
  }

  const originalText = button.textContent;
  button.addEventListener('click', () => {
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        button.textContent = 'Copied!';
        setTimeout(() => { button.textContent = originalText; }, 2000);
      })
      .catch(() => { /* Clipboard API unavailable/denied -- the link itself is still visible in the address bar. */ });
  });
}

interface GoogleFundingChoices {
  callbackQueue: Array<() => void>;
  showRevocationMessage?: () => void;
}

declare global {
  interface Window {
    googlefc?: GoogleFundingChoices;
    gtag?: (...args: unknown[]) => void;
  }
}

const ANALYTICS_CONSENT_KEY = 'outages-analytics-consent';

/**
 * First-party analytics-only consent banner, deliberately decoupled from AdSense's
 * own CMP (wireManageConsent below) -- that one is still pending AdSense account
 * approval and covers ad cookies specifically. This banner is the only thing that
 * ever grants analytics_storage; the <head> script's Consent Mode v2 default stays
 * denied until a visitor answers here. Choice persists in localStorage, per browser,
 * and is re-applied silently on every later page load without showing the banner
 * again.
 */
function wireConsentBanner(): void {
  const bannerEl = document.getElementById('consent-banner');
  const acceptButton = document.getElementById('consent-accept');
  const declineButton = document.getElementById('consent-decline');
  if (!bannerEl || !acceptButton || !declineButton) return;
  const banner = bannerEl;

  function updateConsent(granted: boolean): void {
    window.gtag?.('consent', 'update', { analytics_storage: granted ? 'granted' : 'denied' });
  }

  function choose(granted: boolean): void {
    updateConsent(granted);
    try {
      localStorage.setItem(ANALYTICS_CONSENT_KEY, granted ? 'granted' : 'denied');
    } catch {
      // A private-browsing tab or blocked storage just means we ask again next visit.
    }
    banner.hidden = true;
  }

  acceptButton.addEventListener('click', () => { choose(true); });
  declineButton.addEventListener('click', () => { choose(false); });

  // Only present on the privacy page -- re-opens the banner there without needing
  // to clear cookies/storage manually.
  document.getElementById('manage-analytics-consent')?.addEventListener('click', () => {
    banner.hidden = false;
  });

  let stored: string | null;
  try {
    stored = localStorage.getItem(ANALYTICS_CONSENT_KEY);
  } catch {
    stored = null;
  }

  if (stored === 'granted' || stored === 'denied') {
    updateConsent(stored === 'granted');
    return;
  }

  banner.hidden = false;
}

/**
 * Analytics (via Consent Mode v2, wired in the page shell's <head>) and AdSense's
 * own Google-certified CMP message now handle consent -- see the privacy page. This
 * just wires the footer button to Google's revocation UI so a visitor can change
 * their answer later; it doesn't gate anything itself.
 */
function wireManageConsent(): void {
  const button = document.getElementById('manage-consent');
  if (!button) return;
  button.addEventListener('click', () => {
    window.googlefc?.callbackQueue.push(() => { window.googlefc?.showRevocationMessage?.(); });
  });
}

interface RegisterKeyResponse {
  readonly apiKey: string;
  readonly notice: string;
}
interface ProblemJson {
  readonly detail: string;
}

/** The /developers page's "get an API key" form. Nothing on the map page has this id. */
function wireKeyRegistration(): void {
  const form = document.getElementById('key-form');
  const resultEl = document.getElementById('key-result');
  if (!(form instanceof HTMLFormElement) || !resultEl) return;

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const input = form.elements.namedItem('email');
    if (!(input instanceof HTMLInputElement) || input.value.trim() === '') return;

    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton instanceof HTMLButtonElement) submitButton.disabled = true;

    fetch('/v1/keys', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email: input.value.trim() }),
      cache: 'no-store',
    })
      .then(async (res) => {
        resultEl.hidden = false;
        if (res.ok) {
          const body = (await res.json()) as RegisterKeyResponse;
          resultEl.innerHTML =
            `<strong>Your API key:</strong> <code>${escapeHtml(body.apiKey)}</code>` +
            `<div>${escapeHtml(body.notice)}</div>`;
          resultEl.className = 'postcode-result postcode-result--clear';
          form.reset();
        } else {
          const problem = (await res.json()) as ProblemJson;
          resultEl.textContent = problem.detail;
          resultEl.className = 'postcode-result postcode-result--error';
        }
      })
      .catch(() => {
        resultEl.hidden = false;
        resultEl.textContent = 'Something went wrong. Try again shortly.';
        resultEl.className = 'postcode-result postcode-result--error';
      })
      .finally(() => {
        if (submitButton instanceof HTMLButtonElement) submitButton.disabled = false;
      });
  });
}

function main(): void {
  // The manage-consent button, key-registration form, vote button, incident map and
  // share row each appear on only one page, but all are wired unconditionally,
  // before the live-map-page early return below -- the incident detail page has no
  // #map (the live, polling, nationwide map) at all, just its own #incident-map, a
  // single static marker/shape with nothing to poll.
  wireManageConsent();
  wireKeyRegistration();
  wireConsentBanner();
  wireVoteButtons();
  wireIncidentMap();
  wireIncidentShare();
  wireNewsShare();
  wireIncidentCardMinimaps();
  wireNationAreasTable();

  const mapEl = document.getElementById('map');
  if (!mapEl) return; // not a page with a map

  // /power-cut/:slug (src/web/pages.ts's areaPage) sets these instead of relying on
  // the nationwide default view -- see loadAreaIncidents's own comment for why it
  // also skips the live-fetch/refresh path entirely.
  const areaLon = mapEl.dataset['areaLon'];
  const areaLat = mapEl.dataset['areaLat'];
  const isAreaPage = areaLon !== undefined && areaLat !== undefined;

  // A news article's embedded replay map (src/web/pages.ts's newsReplayMapSection)
  // -- a third, simplest case: no live incidents, no postcode search, just the
  // fixed-window replay itself (wireArticleReplayMap).
  const replayFrom = mapEl.dataset['replayFrom'];
  const replayTo = mapEl.dataset['replayTo'];
  const isArticleReplayPage = replayFrom !== undefined && replayTo !== undefined;

  const map = isAreaPage
    ? L.map(mapEl).setView([Number(areaLat), Number(areaLon)], 13)
    : L.map(mapEl).setView(DEFAULT_CENTER, DEFAULT_ZOOM);
  L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 19,
  }).addTo(map);

  if (isArticleReplayPage) {
    wireArticleReplayMap(map, replayFrom, replayTo);
    return;
  }

  const layer = L.layerGroup().addTo(map);
  wirePostcodeSearch(map);

  if (isAreaPage) {
    loadAreaIncidents(layer, map);
    return;
  }

  // Homepage only -- the ?postcode= handoff target (see applyPostcodeFromQuery's own
  // comment) is specifically /postcode-area's plain navigating form; an area page's
  // own search is already scoped to that area and has no reason to read this.
  applyPostcodeFromQuery(map);
  void loadIncidents(layer);
  void loadStats();
  wireReplayControl(map, layer);

  // Matches src/ingest/http.ts's MIN_POLL_INTERVAL_MS -- keep the two in sync. No
  // point polling the API more often than the backend itself refreshes.
  const REFRESH_INTERVAL_MS = 600_000;
  setInterval(() => { void loadIncidents(layer); void loadStats(); }, REFRESH_INTERVAL_MS);
}

document.addEventListener('DOMContentLoaded', main);
